import { z } from 'zod'
import { requiredPhone } from '@/lib/phone'
import { IS_MAPS_CONFIGURED } from '@/lib/maps-config'
import type { DefaultValues } from 'react-hook-form'
import {
	ADDITIONAL_CATEGORY_VALUES,
	COWORKING_EXTRA_VALUES,
	COWORKING_SERVICE_VALUES,
	EQUIPMENT_VALUES,
	FOOD_AND_DRINK_VALUES,
	KEY_AMENITY_VALUES,
	LAND_CATEGORY_VALUES,
	RESIDENTIAL_PARKING_VALUES,
	ROOM_ADJACENCY_VALUES,
	WALLS_TYPE_VALUES,
} from './enum-options'

const emptyToUndef = (v: unknown) =>
	v === '' || v === null || (typeof v === 'number' && Number.isNaN(v)) ? undefined : v

/**
 * Optional coerced number that treats a cleared input ('') as unset.
 * Plain z.coerce.number() turns '' into 0, which bypasses `=== undefined`
 * required checks and sends bogus zeroes to the API.
 *
 * Non-negative unless the caller says otherwise. Twenty-two fields here were
 * declared with no lower bound, so the form accepted a rent of −50 000, a
 * penalty of −0.08% a day and a flat with −2 rooms. Making the floor the
 * default fixes them together and makes the next field safe without anyone
 * remembering to add it; the three that genuinely go below zero — latitude,
 * longitude and a basement floor — opt out explicitly.
 */
/**
 * Потолок по умолчанию — сколько влезает в самую узкую числовую колонку.
 *
 * Площади объявлены как `Decimal(10, 2)`, то есть до 99 999 999,99; целые —
 * как `Int`, до 2 147 483 647. Тридцать девяток не влезают никуда: Postgres
 * отвергает запись, и пользователь получает ошибку сохранения без объяснения,
 * уже пройдя весь мастер. Граница здесь ловит это на поле, а не в базе.
 *
 * Значения, у которых есть физический смысл — этажи, комнаты, жильцы —
 * ограничены отдельно и строже там, где объявлены.
 */
const COLUMN_CEILING = 99_999_999

export const optionalNumber = (
	schema: z.ZodNumber = z.coerce.number(),
	{ allowNegative = false }: { allowNegative?: boolean } = {}
) =>
	z.preprocess(
		emptyToUndef,
		(allowNegative ? schema : schema.min(0))
			.max(COLUMN_CEILING, 'Слишком большое значение')
			.optional(),
	)

export const step1Schema = z.object({
	type:            z.enum(['APARTMENT', 'HOUSE', 'ROOM', 'OFFICE', 'LAND', 'RETAIL', 'WAREHOUSE', 'GARAGE', 'COWORKING']),
	city:            z.string().trim().min(1, 'Укажите город'),
	address:         z.string().trim().min(1, 'Укажите адрес'),
	apartmentNumber: z.string().trim().optional(),
	ownerType:       z.enum(['OWNER', 'AGENT']),
	phone:           requiredPhone('Укажите корректный телефон'),
	latitude:        optionalNumber(z.coerce.number(), { allowNegative: true }),
	longitude:       optionalNumber(z.coerce.number(), { allowNegative: true }),
})

export const step2Schema = z.object({
	housingType:        z.enum(['APARTMENT', 'APARTMENTS']).optional(),
	// room-specific (type = ROOM)
	roomLocation:       z.enum(['APARTMENT', 'HOSTEL', 'HOTEL']).optional(),
	roomsInApartment:   optionalNumber(z.coerce.number().int()),
	roomHousingType:    z.enum(['ROOM', 'BED']).optional(),
	bedCount:           optionalNumber(z.coerce.number().int()),
	sleepingPlaces:     optionalNumber(z.coerce.number().int()),
	eventsAllowed:      z.boolean().optional(),
	fireplace:          z.boolean().optional(),
	// house-specific (type = HOUSE)
	houseKind:          z.enum(['HOUSE', 'DACHA', 'COTTAGE', 'TOWNHOUSE']).optional(),
	// What the house is built of — `buildingType` asks the same of a block of
	// flats and offers a different list, so the two cannot be shared.
	wallsType:          z.enum(WALLS_TYPE_VALUES).optional(),
	sauna:              z.boolean().optional(),
	pool:               z.boolean().optional(),
	terrace:            z.boolean().optional(),
	plotArea:           optionalNumber(),
	// land-specific (type = LAND)
	landCategory:       z.enum(LAND_CATEGORY_VALUES).optional(),
	bathroomLocations:  z.array(z.enum(['INSIDE', 'OUTSIDE'])).default([]),
	communications:     z.array(z.string()).default([]),
	transportAccess:    z.array(z.string()).default([]),
	nearbyInfrastructure: z.array(z.string()).default([]),

	// ── Коммерческие поля (офис и далее) ─────────────────────────────────
	// Жилые типы их не заполняют, коммерческие не заполняют блок «О доме» от
	// квартиры. Все необязательные: шаг сохраняется черновиком в любой момент.
	onlineViewing:          z.boolean().optional(),
	multipleFloors:         z.boolean().optional(),
	officeLayouts:          z.array(z.enum(['CABINET', 'OPEN'])).default([]),
	partialRent:            z.boolean().optional(),
	ceilingHeight:          optionalNumber(z.coerce.number().positive().max(100, 'Не больше 100 метров')),
	finishing:              z.enum(['NONE', 'CLEAN', 'OFFICE']).optional(),
	powerCapacity:          optionalNumber(z.coerce.number().positive()),
	powerCapacityExpandable: z.boolean().optional(),
	commercialBuildingType: z.enum(['BUSINESS_CENTER', 'SHOPPING_CENTER', 'ADMINISTRATIVE', 'RESIDENTIAL_BUILDING', 'OTHER']).optional(),
	buildingClass:          z.enum(['A', 'B', 'C']).optional(),
	commercialParking:      z.enum(['NONE', 'STREET', 'IN_BUILDING']).optional(),
	parkingFree:            z.boolean().optional(),
	parkingForTrucks:       z.boolean().optional(),
	parkingSpaces:          optionalNumber(z.coerce.number().int().min(0)),

	// ── Торговое и складское ─────────────────────────────────────────────
	entrance:             z.enum(['STREET', 'YARD']).optional(),
	separateEntrance:     z.boolean().optional(),
	commercialHeating:    z.enum(['NONE', 'CENTRAL', 'AUTONOMOUS']).optional(),
	retailPlacement:      z.enum(['STREET_RETAIL', 'IN_SHOPPING_CENTER']).optional(),
	additionalCategories: z.array(z.enum(ADDITIONAL_CATEGORY_VALUES)).default([]),

	// ── Гараж и машиноместо ──────────────────────────────────────────────
	garageKind:       z.enum(['GARAGE', 'PARKING_SPACE']).optional(),
	garageType:       z.enum(['REINFORCED_CONCRETE', 'BRICK', 'METAL']).optional(),
	parkingSpaceType: z.enum(['MULTILEVEL', 'UNDERGROUND', 'COVERED', 'OPEN']).optional(),
	security:         z.boolean().optional(),

	// ── Коворкинг ────────────────────────────────────────────────────────
	seatsCount:         optionalNumber(z.coerce.number().int().min(0)),
	weekendsOpen:       z.boolean().optional(),
	aroundTheClock:     z.boolean().optional(),
	workplaceStatus:    z.enum(['FIXED', 'FLOATING', 'PRIVATE_OFFICE']).optional(),
	privateOfficeArea:  optionalNumber(z.coerce.number().positive()),
	privateOfficeSeats: optionalNumber(z.coerce.number().int().min(0)),
	amenitiesInclusion: z.enum(['INCLUDED', 'NOT_INCLUDED', 'PARTIALLY_INCLUDED']).optional(),
	keyAmenities:       z.array(z.enum(KEY_AMENITY_VALUES)).default([]),
	equipment:          z.array(z.enum(EQUIPMENT_VALUES)).default([]),
	foodAndDrinks:      z.array(z.enum(FOOD_AND_DRINK_VALUES)).default([]),
	coworkingServices:  z.array(z.enum(COWORKING_SERVICE_VALUES)).default([]),
	coworkingExtras:    z.array(z.enum(COWORKING_EXTRA_VALUES)).default([]),
	// Подвал и цоколь — это −1 и −2, поэтому этаж единственный из счётных
	// полей, которому минус разрешён.
	floor:              optionalNumber(z.coerce.number().int().max(200, 'Не больше 200 этажей'), { allowNegative: true }),
	rooms:              optionalNumber(z.coerce.number().int().max(100, 'Не больше 100 комнат')),
	// How the rooms relate to one another, not how many there are.
	roomType:           z.enum(ROOM_ADJACENCY_VALUES).optional(),
	balcony:            z.boolean().optional(),
	loggia:             z.boolean().optional(),
	wardrobe:           z.boolean().optional(),
	// Requiredness (min 1) enforced in addPropertySchema's superRefine, not here:
	// a base-schema type failure (e.g. coercing an empty field) silently skips
	// superRefine entirely, which would suppress the type-conditional checks below.
	totalArea:          optionalNumber(),
	livingArea:         optionalNumber(),
	// Not part of livingArea, which excludes it: Avito asks for the kitchen
	// under a tag of its own.
	kitchenSpace:       optionalNumber(),
	// 'SEVERAL'/'OTHER' retired from the pickers — see from-property.ts.
	bathroomType:       z.enum(['COMBINED', 'SEPARATE']).optional(),
	renovation:         z.enum(['NONE', 'COSMETIC', 'EURO', 'DESIGNER']).optional(),
	furniture:          z.array(z.string()).default([]),
	appliances:         z.array(z.string()).default([]),
	internet:         z.array(z.string()).default([]),
	buildingType:       z.enum(['BRICK', 'PANEL', 'BLOCK', 'MONOLITH', 'MONOLITH_BRICK', 'WOOD']).optional(),
	concierge:       z.boolean().optional(),
	chute:    z.boolean().optional(),
	gas:             z.boolean().optional(),
	yearBuilt:          optionalNumber(z.coerce.number().int()),
	totalFloors:        optionalNumber(z.coerce.number().int().max(200, 'Не больше 200 этажей')),
	passengerElevators: z.enum(['NONE', '1', '2', '3', '4']).optional(),
	freightElevators:   z.enum(['NONE', '1', '2', '3', '4']).optional(),
	yard:               z.array(z.string()).default([]),
	parking:            z.array(z.string()).default([]),
	// Single-valued, unlike `parking`: that one lists a building's facilities,
	// this one answers what kind of parking the property itself has.
	parkingType:        z.enum(RESIDENTIAL_PARKING_VALUES).optional(),
	// Правила заселения (→ PropertyRules)
	maxTenants:       optionalNumber(z.coerce.number().int().max(100, 'Не больше 100 жильцов')),
	childrenAllowed:         z.boolean().optional(),
	petsAllowed:             z.boolean().optional(),
	smokingAllowed:          z.boolean().optional(),
})

/**
 * Step 3 fields — strict subset of Prisma `PropertyRentTerms` model.
 * No invented UI-only flags: toggles (hasDeposit, hasPenalty) live as local
 * useState in the component, not in the form schema. `vatIncluded` is the
 * exception — it is a real column, and the only VAT decision left to the user.
 *
 * DB fields currently not rendered on Figma Step 3 (future phases):
 * - contractType (INDEFINITE | FIXED_TERM)
 * - paymentDay (Int? — day of month)
 * - startDate / endDate (DateTime?)
 */
export const step3Schema = z.object({
	rentAmount:       optionalNumber(),
	currency:         z.enum(['RUB', 'USD', 'EUR']).default('RUB'),
	periodicity:      z.enum(['MONTHLY', 'WEEKLY', 'QUARTERLY', 'YEARLY']).default('MONTHLY'),
	// Ставка выводится сервером из налогового режима; пользователь выбирает
	// только, входит ли НДС в цену — и то лишь при ненулевой ставке.
	vatIncluded:      z.boolean().default(true),
	depositAmount:    optionalNumber(),
	depositCurrency:  z.enum(['RUB', 'USD', 'EUR']).default('RUB'),
	penaltyPercent:   optionalNumber(),
	penaltyGraceDays: optionalNumber(z.coerce.number().int()),
	// One monthly sum for the utilities on top of the rent — whatever step 4
	// does not break down per meter. Avito asks for an amount, not a who-pays
	// answer, so it cannot be folded into PropertyUtility.paidBy.
	otherUtilitiesPayment: optionalNumber(),

	// ── Коммерческая аренда ──────────────────────────────────────────────
	// rentUnit отвечает на «за что берут», periodicity — «как часто платят».
	// Для коммерции ставка за метр в год так же обычна, как помесячная.
	rentUnit:                  z.enum(['PER_MONTH', 'PER_SQM_MONTH', 'PER_YEAR', 'PER_SQM_YEAR', 'PER_SEAT_MONTH']).optional(),
	agentCommissionPercent:    optionalNumber(z.coerce.number().min(0).max(100)),
	leaseKind:                 z.enum(['DIRECT', 'SUBLEASE']).optional(),
	rentHolidays:              z.boolean().optional(),
	minLeaseMonths:            optionalNumber(z.coerce.number().int().min(1)),
	utilitiesIncluded:         z.boolean().optional(),
	operatingExpensesIncluded: z.boolean().optional(),
	// Отдельно от depositAmount: жилую аренду называют суммой, коммерческую —
	// месяцами, и пересчёт одного в другое требует ставки, о которой ещё не
	// договорились.
	// Месяцы аренды числом: залог бывает и в 12 месяцев, перечисление доходило
	// до трёх. Половины сохраняются — о них тоже договариваются.
	depositMonths:             optionalNumber(z.coerce.number().min(0).max(999)),
})

/**
 * Step 4 — Коммунальные платежи.
 * Four utility types → Prisma `PropertyUtility[]` rows, unique by (propertyId, type).
 * Each utility entry is a strict subset of PropertyUtility fields.
 *
 * Utility fields not rendered on Figma (conditional per calculation type on submit):
 * none — all PropertyUtility columns are represented, rendered conditionally
 * based on `calculation` value.
 */
const utilityEntrySchema = z.object({
	calculation:      z.enum(['BY_METER', 'PROPORTIONAL', 'FIXED_PER_SQM', 'FIXED_AMOUNT', 'COEFFICIENT', 'INCLUDED']).optional(),
	paidBy:           z.enum(['TENANT', 'LANDLORD']).optional(),
	hasSeparateMeter: z.boolean().optional(),
	meterNumber:      z.string().optional(),
	propertyArea:     optionalNumber(),
	buildingArea:     optionalNumber(),
	coefficient:      optionalNumber(),
	ratePerSqm:       optionalNumber(),
	fixedAmount:      optionalNumber(),
	currency:         z.enum(['RUB', 'USD', 'EUR']).default('RUB'),
})

export const step4Schema = z.object({
	utilities: z.object({
		ELECTRICITY: utilityEntrySchema,
		WATER:       utilityEntrySchema,
		HEATING:     utilityEntrySchema,
		WASTE:       utilityEntrySchema,
	}),
})

/**
 * Step 5 — Дополнительные услуги.
 * Array of strict PropertyAdditionalService subsets.
 * Fields: name, pricePerUnit, currency, quantity, unit, provisionType, vatIncluded — all Prisma PropertyAdditionalService columns.
 */
const additionalServiceSchema = z.object({
	name:         z.string().trim().optional(),
	// Both were plain optionalNumber(), so the stepper span the price and the
	// quantity below zero and a typed minus was accepted — a service priced at
	// −15 for −13 hours passed straight through to the payload.
	pricePerUnit: optionalNumber(z.coerce.number().min(0, 'properties.services.priceNegative')),
	currency:     z.enum(['RUB', 'USD', 'EUR']).default('RUB'),
	quantity:     optionalNumber(z.coerce.number().positive('properties.services.quantityNotPositive')),
	unit:         z.string().trim().optional(),
	// Обязателен: от него зависит ставка, и «не выбрано» здесь означало бы
	// договориться о цене, не договорившись о налоге.
	provisionType: z.enum(['OWN', 'AGENCY']).optional(),
	// НДС в цене или сверху. Саму ставку клиент не присылает — её выводит
	// сервер из типа оказания и режима владельца.
	vatIncluded:  z.boolean().optional(),
})

export const step5Schema = z.object({
	additionalServices: z.array(additionalServiceSchema).default([]),
})

/**
 * Step 6 — Фото и описание.
 * Strict subset of Prisma Property fields + PropertyImage[] url list.
 *
 * DB fields covered:
 * - Property.description (String?)
 * - Property.videoUrl (String?)
 * - PropertyImage[] — stored as string[] of URLs for Phase 1; upload infra
 *   (storage + endpoint) is a separate PR. The dropzone UI is visual-only
 *   for Phase 1.
 */
export const step6Schema = z.object({
	description: z.string().trim().optional(),
	videoUrl:    z.string().trim().optional(),
	images:      z.array(z.string()).default([]),
})

/**
 * Type-conditional required fields (Phase 1: LAND + APARTMENT).
 * `LAND` needs nothing beyond what's already unconditionally required
 * (address, totalArea) — everything else in the wizard is hidden for it.
 * `APARTMENT` requires a stricter subset of Step 1/2 fields per the Avito spec.
 */
export const addPropertySchema = step1Schema
	.merge(step2Schema)
	.merge(step3Schema)
	.merge(step4Schema)
	.merge(step5Schema)
	.merge(step6Schema)
	.superRefine((values, ctx) => {
		// Coordinates are only ever written by the geocoder — the suggester emits
		// them solely for a building-level hit, and typing in the field clears
		// them. Their absence therefore means the address was never resolved.
		// Checked here rather than in step1Schema: `.superRefine` returns a
		// ZodEffects, which has no `.merge`, and step1Schema is merged four times.
		// Skipped entirely without a maps key: nothing in that build can produce
		// coordinates, so demanding them would make the wizard unsubmittable.
		if (IS_MAPS_CONFIGURED && values.address?.trim() && (values.latitude == null || values.longitude == null)) {
			ctx.addIssue({
				code:    z.ZodIssueCode.custom,
				path:    ['address'],
				message: 'Выберите адрес из подсказок или укажите точку на карте',
			})
		}

		if (values.totalArea === undefined || values.totalArea < 1) {
			ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['totalArea'], message: 'Укажите площадь' })
		}

		// Условия аренды не существуют без ставки: колонка rent_amount не
		// допускает пустого значения, поэтому весь блок молча отбрасывался, если
		// ставку не заполнили. Залог, пеня и коммерческие условия исчезали без
		// единого сообщения — теперь об этом говорят на самом поле.
		const hasRentTerm =
			values.depositAmount != null
			|| values.penaltyPercent != null
			|| values.penaltyGraceDays != null
			|| values.otherUtilitiesPayment != null
			|| values.agentCommissionPercent != null
			|| values.minLeaseMonths != null
			|| values.rentUnit != null
			|| values.leaseKind != null
			|| values.depositMonths != null
			|| values.rentHolidays === true
			|| values.utilitiesIncluded === true
			|| values.operatingExpensesIncluded === true
		if (hasRentTerm && !(values.rentAmount != null && values.rentAmount > 0)) {
			ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['rentAmount'], message: 'Укажите арендную ставку — без неё условия аренды не сохранятся' })
		}

		// Неполная строка услуги отбрасывалась при сборке запроса — без единого
		// слова. Теперь о недостающем говорят на самом поле.
		values.additionalServices?.forEach((service, i) => {
			const touched = Boolean(service.name) || service.pricePerUnit != null
				|| service.quantity != null || Boolean(service.unit)
			if (!touched) return
			const required = [
				['name', service.name],
				['pricePerUnit', service.pricePerUnit],
				['quantity', service.quantity],
			] as const
			for (const [field, value] of required) {
				if (value === undefined || value === null || value === '') {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						path: ['additionalServices', i, field],
						message: 'Заполните поле — иначе услуга не сохранится',
					})
				}
			}
			// Единица тоже обязательна — её требует `CreateAdditionalServiceBody`,
			// и «3000» без «за что» не читается. Но сообщение адресуется
			// количеству: они нарисованы одним блоком с общей рамкой, и место под
			// ошибку там одно. Без этого шаг молча отказывался листаться.
			if (!service.unit) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['additionalServices', i, 'quantity'],
					message: 'Выберите единицу измерения',
				})
			}
		})

		if (values.type === 'APARTMENT') {
			if (!values.apartmentNumber?.trim()) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['apartmentNumber'], message: 'Укажите номер квартиры' })
			}
			if (values.floor === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['floor'], message: 'Укажите этаж' })
			}
			if (values.rooms === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['rooms'], message: 'Укажите количество комнат' })
			}
			if (values.renovation === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['renovation'], message: 'Укажите тип ремонта' })
			}
			if (values.childrenAllowed === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['childrenAllowed'], message: 'Укажите, можно ли с детьми' })
			}
			if (values.petsAllowed === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['petsAllowed'], message: 'Укажите, можно ли с животными' })
			}
		}

		if (values.type === 'ROOM') {
			if (values.roomLocation === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['roomLocation'], message: 'Укажите расположение' })
			}
			if (values.roomLocation === 'APARTMENT' && values.roomsInApartment === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['roomsInApartment'], message: 'Укажите количество комнат в квартире' })
			}
			if (values.roomHousingType === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['roomHousingType'], message: 'Укажите тип жилья' })
			}
			if (values.buildingType === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['buildingType'], message: 'Укажите тип дома' })
			}
			if (values.floor === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['floor'], message: 'Укажите этаж' })
			}
			if (values.totalFloors === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['totalFloors'], message: 'Укажите этажность дома' })
			}
		}

		if (values.type === 'HOUSE') {
			if (values.houseKind === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['houseKind'], message: 'Укажите вид объекта' })
			}
			if (values.buildingType === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['buildingType'], message: 'Укажите тип дома' })
			}
			if (values.totalFloors === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['totalFloors'], message: 'Укажите этажность дома' })
			}
			if (values.rooms === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['rooms'], message: 'Укажите количество комнат' })
			}
			if (values.plotArea === undefined || values.plotArea < 1) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['plotArea'], message: 'Укажите площадь участка' })
			}
			if (values.childrenAllowed === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['childrenAllowed'], message: 'Укажите, можно ли с детьми' })
			}
			if (values.petsAllowed === undefined) {
				ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['petsAllowed'], message: 'Укажите, можно ли с животными' })
			}
		}
	})

export type AddPropertyFormValues = z.infer<typeof addPropertySchema>

export const STEP_1_FIELDS = ['type', 'city', 'address', 'apartmentNumber', 'ownerType', 'phone', 'latitude', 'longitude'] as const
export const STEP_2_FIELDS = [
	'housingType', 'floor', 'rooms', 'balcony', 'loggia', 'wardrobe',
	'roomLocation', 'roomsInApartment', 'roomHousingType', 'bedCount', 'sleepingPlaces', 'eventsAllowed', 'fireplace',
	'houseKind', 'wallsType', 'sauna', 'pool', 'terrace', 'plotArea', 'landCategory', 'bathroomLocations', 'communications', 'transportAccess', 'nearbyInfrastructure',
	'totalArea', 'livingArea', 'kitchenSpace', 'roomType', 'bathroomType', 'renovation',
	'furniture', 'appliances', 'internet',
	'buildingType', 'concierge', 'chute', 'gas',
	'yearBuilt', 'totalFloors', 'passengerElevators', 'freightElevators',
	'yard', 'parking', 'parkingType',
	'maxTenants', 'childrenAllowed', 'petsAllowed', 'smokingAllowed',
	'onlineViewing', 'multipleFloors', 'officeLayouts', 'partialRent',
	'ceilingHeight', 'finishing', 'powerCapacity', 'powerCapacityExpandable',
	'commercialBuildingType', 'buildingClass',
	'commercialParking', 'parkingFree', 'parkingForTrucks', 'parkingSpaces',
	'entrance', 'separateEntrance', 'commercialHeating', 'retailPlacement',
	'additionalCategories',
	'garageKind', 'garageType', 'parkingSpaceType', 'security',
	'seatsCount', 'weekendsOpen', 'aroundTheClock', 'workplaceStatus',
	'privateOfficeArea', 'privateOfficeSeats', 'amenitiesInclusion',
	'keyAmenities', 'equipment', 'foodAndDrinks', 'coworkingServices',
	'coworkingExtras',
] as const
export const STEP_3_FIELDS = [
	'rentAmount', 'currency', 'periodicity', 'vatIncluded',
	'depositAmount', 'penaltyPercent', 'penaltyGraceDays', 'otherUtilitiesPayment',
	'rentUnit', 'agentCommissionPercent', 'leaseKind', 'rentHolidays',
	'minLeaseMonths', 'utilitiesIncluded', 'operatingExpensesIncluded',
	'depositMonths',
] as const
export const STEP_4_FIELDS = ['utilities'] as const
export const STEP_5_FIELDS = ['additionalServices'] as const

export const DEFAULT_VALUES: DefaultValues<AddPropertyFormValues> = {
	type:            'APARTMENT',
	city:            '',
	address:         '',
	apartmentNumber: '',
	ownerType:       'OWNER',
	phone:           '',
	furniture:       [],
	appliances:      [],
	internet:      [],
	yard:            [],
	parking:         [],
	currency:        'RUB',
	depositCurrency: 'RUB',
	periodicity:     'MONTHLY',
	vatIncluded:     true,
	// Массив с default([]) в zod требует значения и в defaultValues, иначе RHF
	// отдаёт undefined и чекбоксы падают на неконтролируемый ввод.
	officeLayouts:   [],
	bathroomLocations: [],
	additionalCategories: [],
	keyAmenities:      [],
	equipment:         [],
	foodAndDrinks:     [],
	coworkingServices: [],
	coworkingExtras:   [],
	utilities: {
		ELECTRICITY: { calculation: 'BY_METER',      currency: 'RUB' },
		WATER:       { calculation: 'PROPORTIONAL',  currency: 'RUB' },
		HEATING:     { calculation: 'FIXED_PER_SQM', currency: 'RUB' },
		// The other three are Selects that default to a common calculation.
		// Waste is a toggle, so a preset `calculation` would render it already
		// switched on and make the first click switch it off — and every new
		// property would silently carry a waste utility with no amount.
		WASTE:       { currency: 'RUB' },
	},
	additionalServices: [
		{ currency: 'RUB', provisionType: 'OWN', vatIncluded: true },
	],
	images: [],
}
